import { LogeMember } from "../../../types/entities";

// How the 22 fields of the wheel are decided, and in what order they sit
// around it. Pure data — no canvas, no WebGPU — so the arrangement can be
// reasoned about (and tested) on its own.

/** Fixed by the game: 21 member fields + the beer. */
export const SLOT_COUNT = 22;
export const MEMBER_SLOTS = SLOT_COUNT - 1;
/** How many times each member appears on a full seven-member wheel. */
export const COPIES_PER_MEMBER = 3;

export const BEER_EMOJI = "🍺";
export const BEER_LABEL = "Øl";
/** Stand-in for a member who hasn't picked an emoji yet. */
export const DEFAULT_EMOJI = "🙂";

export type RosterMember = Pick<LogeMember, "id" | "name" | "emoji">;

/** One distinct face on the wheel. Its index doubles as its texture layer. */
export interface WheelEntry {
  key: string;
  emoji: string;
  label: string;
  kind: "member" | "beer";
  memberId?: LogeMember["id"];
  /** How many of the 22 fields this face occupies. */
  copies: number;
}

export interface WheelLayout {
  /** Distinct faces: the members, then the beer. */
  entries: WheelEntry[];
  /** One entry index per field, going round the wheel from field 0. */
  slots: number[];
}

export const TAU = Math.PI * 2;

/** The centre of a field in the wheel's own frame, radians. */
export function slotAngle(slot: number): number {
  return (TAU * (slot + 0.5)) / SLOT_COUNT;
}

/**
 * How many fields each member gets.
 *
 * The game is written for the seven-member loge, where the arithmetic is
 * exact: 7 × 3 + 1 beer = 22. The roster is a live table though, so this
 * spreads the 21 member fields as evenly as it can over whatever roster it is
 * handed rather than refusing to build a wheel — with seven members that is
 * three each and nothing else, and with any other count everyone still gets a
 * field, with the remainder handed out one at a time. `order` decides who
 * collects a remainder, so a caller that shuffles keeps that fair over time.
 */
export function shareFields(memberCount: number): number[] {
  if (memberCount <= 0) return [];
  const base = Math.floor(MEMBER_SLOTS / memberCount);
  const remainder = MEMBER_SLOTS % memberCount;
  return Array.from({ length: memberCount }, (_, i) => base + (i < remainder ? 1 : 0));
}

/**
 * Deals the fields out around the wheel so a member's copies end up spread
 * across it rather than sitting in a block — the same reason a real wheel
 * doesn't run 1-2-3-4 round the rim. One pass hands every member a single
 * field, so on the seven-member wheel a member's three fields land exactly a
 * roster apart, seven fields from each other.
 */
function dealAround(counts: number[]): number[] {
  const passes = Math.max(...counts, 0);
  const sequence: number[] = [];
  for (let pass = 0; pass < passes; pass++) {
    for (let member = 0; member < counts.length; member++) {
      if (counts[member] > pass) sequence.push(member);
    }
  }
  return sequence;
}

/**
 * Nudges apart the one case an uneven roster can still leave behind: two
 * fields of the same face touching where the last pass runs short. Swapping
 * one of them with a field further round fixes it without changing anyone's
 * number of fields. Field 0 is the beer and never moves.
 */
function separateNeighbours(slots: number[], pinned: number): number[] {
  const out = [...slots];
  const n = out.length;
  const clashes = (values: number[], i: number) =>
    values[i] === values[(i - 1 + n) % n] || values[i] === values[(i + 1) % n];

  for (let i = 0; i < n; i++) {
    if (i === pinned || !clashes(out, i)) continue;
    for (let step = 2; step < n; step++) {
      const j = (i + step) % n;
      if (j === pinned || out[j] === out[i]) continue;
      const swapped = [...out];
      swapped[i] = out[j];
      swapped[j] = out[i];
      if (!clashes(swapped, i) && !clashes(swapped, j)) {
        out[i] = swapped[i];
        out[j] = swapped[j];
        break;
      }
    }
  }
  return out;
}

/**
 * Builds the 22 fields from a roster. The beer takes field 0 — the wheel's
 * zero — and the members fill the rest.
 */
export function buildWheelLayout(members: RosterMember[]): WheelLayout {
  const counts = shareFields(members.length);
  const entries: WheelEntry[] = members.map((m, i) => ({
    key: `member-${m.id}`,
    emoji: m.emoji ?? DEFAULT_EMOJI,
    label: m.name,
    kind: "member" as const,
    memberId: m.id,
    copies: counts[i],
  }));
  const beerIndex = entries.length;
  entries.push({
    key: "beer",
    emoji: BEER_EMOJI,
    label: BEER_LABEL,
    kind: "beer",
    copies: 1,
  });

  const slots = members.length ? [beerIndex, ...dealAround(counts)] : [beerIndex];
  return { entries, slots: separateNeighbours(slots, 0) };
}

export type PocketTone = "ink" | "clay" | "gold";

/**
 * The field's colour. The beer is the wheel's zero and gets the one gold
 * pocket; everything else alternates dark and rust the whole way round. The
 * member fields are an odd count, which is exactly why the alternation is
 * indexed from field 1 rather than 0 — it leaves the gold pocket with a
 * different neighbour on each side instead of closing the ring on a repeat.
 */
export function pocketTone(slot: number, layout: WheelLayout): PocketTone {
  if (layout.entries[layout.slots[slot]]?.kind === "beer") return "gold";
  return slot % 2 === 1 ? "ink" : "clay";
}
